import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../static/styles/WeeklyTournaments.module.css';
import calendarIcon from '../static/img/Home/calendarIcon.svg';
import { fetchWeeklyTournaments, fetchEslTournaments, eslTournamentCheckin, eslTournamentMatchCheckin } from '../api/tournaments';

import ArrowLeft from '../static/img/backArrow.png'
import LeaderBoard from '../static/img/Sidebar/iconLeaderboard.svg'
import pubgIcon from '../static/img/DailyGamingTasks/iconPubgDailyTasks.png';
import apexIcon from '../static/img/DailyGamingTasks/iconApexDailyTasks.avif';
import codIcon from '../static/img/DailyGamingTasks/iconCODDailyTasks.avif';
import freeFireIcon from '../static/img/DailyGamingTasks/iconFreeFireDailyTasks.avif';
import MessagePopup from '../components/MessagePopup';
import CannonClashInstructions from '../components/CannonClashInstructions';
import AimTrainInstructions from '../components/AimTrainInstructions';
import TournamentRulesPopup from '../components/TournamentRulesPopup';
import popupStyles from '../static/styles/MessagePopup.module.css';
import cannonClashCover from '../static/img/MadeByESL/coverCannonClash.avif';
import aimTrainCover from '../static/img/MadeByESL/coverAimTrainTour.avif';
import aimTrainRamadan from '../static/img/MadeByESL/AimTrain_Ramadan.avif';

// Import static background images
import pubgBg from '../static/img/WeeklyTournaments/imagePubgTour.avif';
import freeFireBg from '../static/img/WeeklyTournaments/imageFreeFireTour.avif';
import { jwtDecode } from 'jwt-decode';
import { trackTournamentSignup } from '../utils/analytics';

const getGameIcon = (gameName) => {
    switch (gameName.toLowerCase()) {
        case 'pubg mobile':
            return pubgIcon;
        case 'freefire':
            return freeFireIcon;
        // Add cases for other games like COD, APEX LEGENDS
        default:
            return null; // Or a default icon
    }
};

// Map game names to static background images
const getGameBackground = (gameName) => {
    switch (gameName.toLowerCase()) {
        case 'pubg mobile':
            return pubgBg;
        case 'freefire':
            return freeFireBg;
        // Add cases for other games
        default:
            return null; // Fallback or default background
    }
};

// Helper function to format date/time
const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    const [datePart, timePart, ampm] = dateTimeString.split(' ');
    return {
        date: datePart,
        time: `${timePart} ${ampm}`
    };
};

// Helper function to format match time (24-hour format)
const formatMatchTime = (dateTimeString) => {
    if (!dateTimeString) return { date: 'N/A', time: 'N/A' };
    const [datePart, timePart] = dateTimeString.split(' ');
    // Convert 24-hour to 12-hour format
    const [hours, minutes] = timePart.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 % 12 || 12;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return {
        date: datePart,
        time: `${hour12}:${minutes} ${ampm}`
    };
};

const WeeklyTournaments = ({ isJcUser }) => {
    const [tournaments, setTournaments] = useState([]);
    const [eslTournaments, setEslTournaments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showRoomPopup, setShowRoomPopup] = useState(false);
    const [roomId, setRoomId] = useState('');
    const [selectedTournament, setSelectedTournament] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [showInstructions, setShowInstructions] = useState(false);
    const [showAimTrainInstructions, setShowAimTrainInstructions] = useState(false);
    const [showTournamentRules, setShowTournamentRules] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        document.title = "Tournaments | Jazz Esl"

        // Check if user is logged in and decode JWT
        const accessToken = sessionStorage.getItem('access_token');
        const msisdn = sessionStorage.getItem('msisdn');
        setIsLoggedIn(!!accessToken && !!msisdn);



        const loadTournaments = async () => {
            try {
                setLoading(true);
                setError(null);
                const [weeklyData, eslData] = await Promise.all([
                    fetchWeeklyTournaments(),
                    fetchEslTournaments()
                ]);
                setTournaments(weeklyData);
                setEslTournaments(eslData);
            } catch (err) {
                setError(err.message || 'Failed to load tournaments');
            } finally {
                setLoading(false);
            }
        };

        loadTournaments();
    }, []);

    if (loading) {
        return <div className={styles.loading}>Loading tournaments...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    const handleJoinTournament = (tournamentId, tournamentName = 'Weekly Tournament') => {
        navigate(`/join-tournament?tournamentId=${tournamentId}`);
    };

    const handleEslTournamentJoin = async (tournament) => {
        if (!isLoggedIn) {
            setPopupMessage('Please log in to participate in tournaments');
            setPopupOpen(true);
            return;
        }

        if (isLoggedIn && tournament.registered === 1 && tournament.is_running !== 1) {
            return;
        }

        if (tournament.tourn_mode === 'special') {
            if (tournament.is_running === 1 || tournament.status === 2) {
                setSelectedTournament(tournament);
                setShowRoomPopup(true);
                return;
            }

            try {
                const response = await eslTournamentCheckin(tournament.tourn_id);
                if (response.data?.already_submited === true) {
                    setPopupMessage('You are already registered for this tournament. Wait for it to start.');
                    setPopupOpen(true);
                    return;
                } else {
                    trackTournamentSignup(tournament.tourn_name, tournament.tourn_id, 'ESL', tournament.tournament_game);
                    setPopupMessage('Successfully joined the tournament! Check the instructions below for how to play.');
                    setPopupOpen(true);
                }
            } catch (error) {
            }
            // navigate('/join-esl-tournament', { state: { tournament } });
            return;
        }

        try {
            if (tournament.is_running === 1) {
                setSelectedTournament(tournament);
                setShowRoomPopup(true);
                return;
            }

            const response = await eslTournamentCheckin(tournament.tourn_id);

            if (response.data.success === false) {
                setPopupMessage(response.data.message);
                setPopupOpen(true);
            }

            if (response.data.already_submited === true) {
                if (tournament.is_running === 1) {
                    setSelectedTournament(tournament);
                    setShowRoomPopup(true);
                } else {
                    setPopupMessage('You are already registered for this tournament. Wait for it to start.');
                    setPopupOpen(true);
                }
            } else {
                trackTournamentSignup(tournament.tourn_name, tournament.tourn_id, 'ESL', tournament.tournament_game);

                const isCannonClash = tournament.tournament_game === "cannon_clash_001";
                if (isCannonClash) {
                    setPopupMessage('Successfully joined the tournament! Check the instructions below for how to play.');
                    setPopupOpen(true);
                    setTimeout(() => {
                        setShowInstructions(true);
                    }, 1500);
                } else {
                    setPopupMessage('Successfully joined the tournament!');
                    setPopupOpen(true);
                }
            }
        } catch (error) {
            const message =
                error.response?.data?.data?.message ||
                error.response?.data?.message ||
                error.message ||
                'Failed to join tournament';

            setPopupMessage(message);
            setPopupOpen(true);
        }
    };

    const handleRoomIdSubmit = async () => {
        if (!roomId.trim() || !selectedTournament) return;

        try {
            const response = await eslTournamentMatchCheckin(selectedTournament.tourn_id, roomId.trim());
            //console.log('Match Checkin Response:', response);

            // Check for both 'success' and 'successss' (typo in API)
            const isSuccess = response.success === true || response.successss === true;

            if (!isSuccess) {
                // Case 1: Error (too early, invalid room ID, etc.)
                setPopupMessage(response.message || 'Failed to check in to match');
                setPopupOpen(true);
            } else {
                // Case 2 & 3: Navigate to game with roomId
                const gameRoute = selectedTournament.tournament_game === "aim_train_001" ? '/aim-train' : '/cannon-clash';
                navigate(gameRoute, {
                    state: {
                        roomId: roomId.trim(),
                        tournamentId: selectedTournament.tourn_id,
                        startGame: response.start_game,
                        tournamentMode: true,
                        roundNumber: response.round_number
                    }
                });
            }
        } catch (error) {
            //console.error('Match Checkin Error:', error);
            setPopupMessage(error.message || 'Failed to check in to match');
            setPopupOpen(true);
        } finally {
            setShowRoomPopup(false);
            setRoomId('');
            setSelectedTournament(null);
        }
    };

    const handleRoomPopupClose = () => {
        setShowRoomPopup(false);
        setRoomId('');
        setSelectedTournament(null);
    };

    const handlePopupClose = () => {
        setPopupOpen(false);
    };

    const handleInstructionsClose = () => {
        setShowInstructions(false);
    };

    const handleAimTrainInstructionsClose = () => {
        setShowAimTrainInstructions(false);
    };

    const handleTournamentRulesClose = () => {
        setShowTournamentRules(false);
    };

    const handleShowInstructions = (tournament) => {
        if (tournament.tournament_game === "cannon_clash_001") {
            setShowInstructions(true);
        } else if (tournament.tournament_game === "aim_train_001") {
            setShowAimTrainInstructions(true);
        }
    };

    const liveEslTournaments = eslTournaments.filter(t => {
        if (t.is_live !== 1) return false;
        // Hide special tournaments for JC users (is_jc === 1)
        if (isJcUser && t.tourn_mode === 'special') return false;
        return true;
    });

    const gradients = [
        "linear-gradient(135deg, #3D2200, #1A0E00)",
        "linear-gradient(135deg, #0A283D, #000A1A)",
        "linear-gradient(135deg, #3D0A0A, #1A0000)",
        "linear-gradient(145deg, #3A1A8A, #10082A)",
        "linear-gradient(135deg, #0A3A4A, #001A28)",
        "linear-gradient(135deg, #0D3A14, #001408)",
    ];


    return (
        <div className={styles.container}>
            <div className={styles.navigationHeader}>
                <div
                    className={styles.navigationBack}
                    onClick={() => navigate(-1)}
                >
                    <img src={ArrowLeft} alt="<" />
                </div>
                <div className={styles.navigationTitle}>
                    <p>Weekly</p>
                    <h2 className={styles.heading}>tournaments</h2>
                </div>
            </div>
            <div className={styles.gamingTaskHeader}>
                <p className={styles.subheading}>play live tournaments</p>
                <h2 className={styles.heading}>Weekly and daily tournaments</h2>
                <div className={styles.headerstatsContainer}>
                    <div className={styles.headerstats}>
                        <span>{tournaments.length}</span>
                        <p>Tournaments</p>
                    </div>
                    <div className={styles.headerstats}>
                        <span>{eslTournaments.length}</span>
                        <p>Esl exclusive</p>
                    </div>
                    <div className={styles.headerstats}>
                        {isJcUser ? (
                            <span>FREE</span>
                        ) : (
                            <span>50</span>
                        )}
                        {isJcUser ? (
                            <p>Entry</p>
                        ) : (
                            <p>Entry credits</p>
                        )}
                    </div>
                </div>
            </div>

            {liveEslTournaments.length > 0 && (
                <div className={styles.eslSection}>
                    <div className={styles.sectionSeparator}>
                        <span className={styles.sectionSeparatorText}>
                            ESL EXCLUSIVE
                        </span>
                    </div>
                    <div className={styles.cardGrid}>
                        {liveEslTournaments.map((tournament, index) => {
                            const tournamentImage =
                                tournament.tourn_mode === "special"
                                    ? aimTrainRamadan
                                    : tournament.tournament_game === "cannon_clash_001"
                                        ? cannonClashCover
                                        : tournament.tournament_game === "aim_train_001"
                                            ? aimTrainCover
                                            : tournament.thumbnail;
                            return (
                                <div key={tournament.Id} className={styles.cardWrapper}>
                                    <div className={styles.card} style={{
                                        "--bg": gradients[index % gradients.length],
                                    }}>
                                        <div className={styles.cardImage}>
                                            <img
                                                src={tournamentImage}
                                                alt={tournament.tournament_game}
                                            />
                                            {tournament.status === 2 && (
                                                <div className={styles.liveBadge}>
                                                    <span className={styles.liveDot} />
                                                    LIVE
                                                </div>
                                            )}
                                        </div>
                                        <div className={styles.cardGameInfo}>
                                            <p>
                                                {tournament.status === 4
                                                    ? "Tournament Ended"
                                                    : tournament.is_running === 1
                                                        ? `Live • ${formatDateTime(tournament.time).time}`
                                                        : `Starts ${formatDateTime(tournament.time).date} ${formatDateTime(tournament.time).time}`
                                                }
                                            </p>

                                            <span>
                                                {tournament.tournament_game === "aim_train_001"
                                                    ? "Aim Train"
                                                    : tournament.tournament_game === "cannon_clash_001"
                                                        ? "Cannon Clash"
                                                        : "Tournament"}
                                            </span>


                                            {isJcUser ? (
                                                <h5>Free Entry</h5>
                                            ) : (
                                                <h5>Entry Fee: {tournament.entryFee != null
                                                    ? `${tournament.entryFee} Credits`
                                                    : tournament.isEntryFree === false
                                                        ? "Entry N/A"
                                                        : "Free Entry"}</h5>
                                            )}

                                            <div className={styles.infoRow}>

                                                {/* <h2>
                                                    {tournament.entryFee != null
                                                        ? `fee: ${tournament.entryFee} Credits`
                                                        : tournament.isEntryFree === 1
                                                            ? "Free"
                                                            : "N/A"}
                                                </h2> */}

                                                {tournament.prize && (
                                                    <h2>
                                                        Reward: {tournament.prize}
                                                    </h2>
                                                )}

                                                {tournament.total_checkin && (
                                                    <h2>
                                                        Players: {tournament.total_checkin}
                                                    </h2>
                                                )}

                                            </div>

                                            {isLoggedIn &&
                                                tournament.registered === 1 &&
                                                tournament.match_time && (
                                                    <div className={styles.infoRow}>
                                                        <h2>
                                                            Match:
                                                            {" "}
                                                            {formatMatchTime(
                                                                tournament.match_time
                                                            ).date}
                                                        </h2>

                                                        <h2>
                                                            {
                                                                formatMatchTime(
                                                                    tournament.match_time
                                                                ).time
                                                            }
                                                        </h2>
                                                    </div>
                                                )}

                                            {isLoggedIn &&
                                                tournament.registered === 1 &&
                                                tournament.room_id && (
                                                    <div className={styles.infoRow}>
                                                        <h2>
                                                            Room ID:
                                                            {" "}
                                                            {tournament.room_id}
                                                        </h2>
                                                    </div>
                                                )}

                                            <div className={styles.bottomButtons}>
                                                {tournament.status === 4 ? (
                                                    <button
                                                        className={styles.playButton}
                                                        disabled
                                                    >
                                                        Ended
                                                    </button>
                                                ) : (
                                                    <button
                                                        className={styles.playButton}
                                                        onClick={() =>
                                                            handleEslTournamentJoin(tournament)
                                                        }
                                                    >
                                                        {!isLoggedIn
                                                            ? "Login Required"
                                                            : tournament.is_running === 1
                                                                ? "Play Now"
                                                                : tournament.registered === 1
                                                                    ? "Registered"
                                                                    : "Join Now"}
                                                    </button>
                                                )}

                                                {tournament.tourn_mode === "special" &&
                                                    tournament.status !== 4 && (
                                                        <button
                                                            className={styles.smallButton}
                                                            onClick={() =>
                                                                setShowTournamentRules(true)
                                                            }
                                                            title='Rules'
                                                        >
                                                            Rules
                                                        </button>
                                                    )}

                                                {tournament.is_running === 1 && (
                                                    <button
                                                        className={styles.smallButton}
                                                        onClick={() =>
                                                            navigate(
                                                                `/tournament/${tournament.tourn_id}`
                                                            )
                                                        }
                                                        title='Leaderboard'
                                                    >
                                                        <img src={LeaderBoard} alt="" />
                                                    </button>
                                                )}

                                                {(tournament.tournament_game ===
                                                    "cannon_clash_001" ||
                                                    tournament.tournament_game ===
                                                    "aim_train_001") &&
                                                    tournament.status !== 4 && (
                                                        <button
                                                            className={styles.smallButton}
                                                            onClick={() =>
                                                                handleShowInstructions(tournament)
                                                            }
                                                            title='How to play'
                                                        >
                                                            ❔
                                                        </button>
                                                    )}

                                            </div>

                                        </div>

                                    </div>

                                </div>
                            );
                        })}

                    </div>
                </div>
            )}

            {eslTournaments.length > 0 && tournaments.length > 0 && (
                <div className={styles.sectionSeparator}>
                    <span className={styles.sectionSeparatorText}>Weekly Tournaments</span>
                </div>
            )}

            <div className={styles.cardGrid}>
                {tournaments.map((tournament, index) => {
                    const { date, time } = formatDateTime(tournament.time);
                    const isCompleted = tournament.status !== 1 || tournament.is_running === 1;
                    const isLive = tournament.status === 2;
                    const gameBgSrc = getGameBackground(tournament.tourn_name);
                    return (
                        <div
                            key={tournament.id}
                            className={styles.cardWrapper}
                        >

                            {/* MAIN CARD */}
                            <div
                                className={styles.card}
                                style={{
                                    "--bg":
                                        gradients[index % gradients.length],
                                }}
                            >

                                {/* IMAGE */}
                                <div className={styles.cardImage}>
                                    <img
                                        src={gameBgSrc}
                                        alt={tournament.tourn_name}
                                    />
                                    {isLive && (
                                        <div className={styles.liveBadge}>
                                            <span className={styles.liveDot} />
                                            LIVE
                                        </div>
                                    )}
                                </div>


                                {/* INFO */}
                                <div className={styles.cardGameInfo}>
                                    <p>
                                        {isCompleted
                                            ? "Tournament Ended"
                                            : `Starts ${date} ${time}`}
                                    </p>

                                    <span>
                                        {tournament.tourn_name}
                                    </span>

                                    <h5>Participents: {tournament.entries}</h5>
                                    
                                    {isJcUser ? (
                                        <h5>Free Entry</h5>
                                    ) : (
                                        <h5>Entry Fee: {tournament.entryFee != null
                                            ? `${tournament.entryFee} Credits`
                                            : tournament.isEntryFree === false
                                                ? "Entry N/A"
                                                : "Free Entry"}</h5>
                                    )}

                                    {/* INFO ROW */}
                                    <div className={styles.infoRow}>
                                        {/* <h2>
                                            {tournament.entryFee != null
                                                ? `Fee: ${tournament.entryFee}`
                                                : tournament.isEntryFree === false
                                                    ? "Entry N/A"
                                                    : "Free Entry"}
                                        </h2> */}

                                        {tournament.prize && (
                                            <h2>
                                                1st: {tournament.prize}
                                            </h2>
                                        )}
                                        {tournament.second_prize && (
                                            <h2>
                                                2nd: {tournament.second_prize}
                                            </h2>
                                        )}
                                        {tournament.third_prize && (
                                            <h2>
                                                3rd: {tournament.third_prize}
                                            </h2>
                                        )}

                                    </div>

                                    {/* BUTTONS BELOW */}
                                    <div className={styles.bottomButtons}>

                                        {isCompleted ? (
                                            <button
                                                className={styles.playButton}
                                                disabled
                                            >
                                                Ended
                                            </button>
                                        ) : (
                                            <button
                                                className={styles.playButton}
                                                onClick={() =>
                                                    handleJoinTournament(
                                                        tournament.tourn_id
                                                    )
                                                }
                                            >
                                                Join Now
                                            </button>
                                        )}

                                    </div>

                                </div>

                            </div>



                        </div>

                    );
                })}

            </div>

            {/* Room ID Popup */}
            {showRoomPopup && (
                <div className={popupStyles.popupOverlay}>
                    <div className={popupStyles.popupContent}>
                        <h3>Enter Room ID</h3>
                        <p>You are already checked in to this tournament. Please enter your room ID to continue:</p>
                        <input
                            type="text"
                            value={roomId}
                            onChange={(e) => setRoomId(e.target.value)}
                            placeholder="Enter room ID"
                            className={popupStyles.roomInput}
                        />
                        <div className={popupStyles.popupButtons}>
                            <button onClick={handleRoomIdSubmit} className={popupStyles.submitButton}>
                                Submit
                            </button>
                            <button onClick={handleRoomPopupClose} className={popupStyles.cancelButton}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <MessagePopup
                isOpen={popupOpen}
                message={popupMessage}
                onClose={handlePopupClose}
            />

            <CannonClashInstructions
                isOpen={showInstructions}
                onClose={handleInstructionsClose}
            />

            <AimTrainInstructions
                isOpen={showAimTrainInstructions}
                onClose={handleAimTrainInstructionsClose}
            />

            <TournamentRulesPopup
                isOpen={showTournamentRules}
                onClose={handleTournamentRulesClose}
            />
        </div>
    );
};

export default WeeklyTournaments; 